// The profile strip at the bottom of the side menu: initials, name, "<role> · <organisation>" and a chevron. Clicking
// it opens a small menu upwards: the person's organisations (when they have two or more: the current one ticked, click
// to switch), My Account, Help (with the What's new count), Contact support, and Sign out. The Falcon owner's strip
// is gold with a "Falcon owner" badge; inside an organisation it says "Viewing <organisation> (support access)" and
// the menu starts with "Back to Falcon Console".
// Closed on every new page; click / Esc / a click elsewhere closes it; arrow keys move through it.
import { esc, initials, post, toast } from "./lib.js";
import { fullName, onAction, state } from "./state.js";

const CHEVRON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 10l4-4 4 4M8 14l4 4 4-4"/></svg>';
const TICK = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>';

/** The person's organisations they can open (deactivated places and suspended organisations aren't offered). */
export const openableWorkspaces = () => (state.session.workspaces || []).filter((w) => w.available);

// ---- "Remember my choice on this device" ------------------------------------------------------------------------
const rememberKey = () => `falcon_org_choice:${state.session.user?.id || ""}`;
/** The organisation remembered on this device for this person, if they asked for it. */
export function rememberedOrg(): string | null {
  try { return localStorage.getItem(rememberKey()); } catch { return null; }
}
export function rememberOrg(orgId: string | null): void {
  try { if (orgId) localStorage.setItem(rememberKey(), orgId); else localStorage.removeItem(rememberKey()); } catch { /* private window */ }
}

/** Open another organisation: a fresh page load on its Home, so nothing from the one left stays on screen. */
export async function switchOrganisation(orgId: string): Promise<void> {
  await post("/api/session/workspace", { orgId });
  if (rememberedOrg()) rememberOrg(orgId);
  try { sessionStorage.removeItem("falcon_return"); } catch { /* private window */ }
  location.assign("/app");
}

export function profileHtml(inConsole: boolean, _active: string): string {
  const s = state.session;
  const org = s.organisation;
  const owner = !!s.user?.isPlatformOwner;
  const name = fullName(s.user);
  const sub = owner
    ? (inConsole ? "Falcon Console" : `Viewing ${org?.name || "an organisation"} (support access)`)
    : [s.membership?.role || "", org?.name || ""].filter(Boolean).join(" · ");
  const orgs = !owner ? openableWorkspaces() : [];
  const item = (label: string, attrs: string) => `<a role="menuitem" class="falcon-profile-item" ${attrs}>${label}</a>`;
  const helpHref = inConsole ? "/console/help" : "/app/help";
  const items = [
    orgs.length > 1 ? `<div class="falcon-profile-section" role="group" aria-label="Organisation"><div class="falcon-profile-heading" aria-hidden="true">Organisation</div>${orgs.map((w) => {
      const here = !inConsole && org?.id === w.orgId;
      return `<button type="button" role="menuitemradio" aria-checked="${here}" class="falcon-profile-item falcon-profile-org${here ? " current" : ""}" data-action="profile-switch" data-org="${esc(w.orgId)}"><span class="falcon-profile-tick">${here ? TICK : ""}</span><span class="falcon-profile-org-name">${esc(w.name)}</span><small>${esc(w.role)}</small></button>`;
    }).join("")}</div><div class="falcon-profile-divider" role="separator"></div>` : "",
    owner && !inConsole ? item("Back to Falcon Console", 'href="/console" data-action="back-to-console"') : "",
    item("My Account", `href="${inConsole ? "/console/account" : "/app/account"}" data-link`),
    item(`Help<span class="falcon-profile-count" id="profile-help-count" hidden></span>`, `href="${helpHref}" data-link`),
    !owner && !inConsole && org ? item("Contact support", 'href="/app/help/support" data-action="contact-support"') : "",
    '<div class="falcon-profile-divider" role="separator"></div>',
    `<button type="button" role="menuitem" class="falcon-profile-item" data-action="sign-out">Sign out</button>`,
  ].join("");
  return `<div id="current-user" class="falcon-profile${owner ? " owner" : ""}">
  ${owner && !inConsole ? '<div class="falcon-profile-support">Support access: actions are logged</div>' : ""}
  <div class="falcon-profile-menu" id="profile-menu" role="menu" aria-label="Your account" hidden>${items}</div>
  <button type="button" class="falcon-profile-strip" id="profile-strip" data-action="toggle-profile" aria-haspopup="menu" aria-expanded="false" aria-controls="profile-menu" title="${esc(name)}${sub ? ` · ${esc(sub)}` : ""}">
    <span class="falcon-profile-avatar" aria-hidden="true">${esc(initials(name))}</span>
    <span class="falcon-nav-badge falcon-profile-badge" id="nav-help-badge" title="New in What's new" hidden></span>
    <span class="falcon-profile-text"><span class="falcon-profile-name"><span class="falcon-profile-name-text">${esc(name)}</span>${owner ? '<span class="falcon-owner-badge">Falcon owner</span>' : ""}</span><span class="falcon-profile-sub">${esc(sub)}</span></span>
    <span class="falcon-profile-chevron">${CHEVRON}</span>
  </button>
</div>`;
}

// ---- Opening and closing ----------------------------------------------------------------------------------------
/** Before the side menu is redrawn: was the menu open, on which page, and was the keyboard inside it? */
export function profileOpenState(): { at: string; focus: boolean } | null {
  const m = menu();
  if (!m || m.hidden) return null;
  return { at: location.pathname + location.search, focus: !!m.contains(document.activeElement) };
}
/** After a redraw of the same page (a screen that draws itself again once its data arrives), the menu stays open.
 *  A new page always starts with it closed. */
export function restoreProfile(was: { at: string; focus: boolean } | null): void {
  if (was && was.at === location.pathname + location.search) openProfile(was.focus);
}
const menu = () => document.getElementById("profile-menu");
const strip = () => document.getElementById("profile-strip");
const menuItems = () => Array.from(menu()?.querySelectorAll<HTMLElement>('[role="menuitem"], [role="menuitemradio"]') || []);

/** The menu is placed on the screen next to the strip (above it; below when there isn't room), so the side menu's
 *  scrolling or a narrow (icons only) side menu never cut it off. */
function place(): void {
  const m = menu(), b = strip();
  if (!m || !b) return;
  const r = b.getBoundingClientRect();
  const narrow = r.width < 120;
  const width = Math.max(narrow ? 240 : r.width, 220);
  m.style.position = "fixed";
  m.style.width = `${Math.min(width, window.innerWidth - 16)}px`;
  m.style.left = `${Math.max(8, Math.min(narrow ? r.right + 8 : r.left, window.innerWidth - width - 8))}px`;
  const h = m.offsetHeight;
  if (r.top - h - 6 >= 8) { m.style.top = ""; m.style.bottom = `${window.innerHeight - r.top + 6}px`; }
  else { m.style.bottom = ""; m.style.top = `${Math.min(r.bottom + 6, window.innerHeight - h - 8)}px`; }
}
export function closeProfile(focusStrip = false): void {
  const m = menu();
  if (!m || m.hidden) return;
  m.hidden = true;
  strip()?.setAttribute("aria-expanded", "false");
  if (focusStrip) strip()?.focus();
}
function openProfile(focusFirst: boolean): void {
  const m = menu();
  if (!m) return;
  m.hidden = false;
  strip()?.setAttribute("aria-expanded", "true");
  const c = document.getElementById("profile-help-count"), b = document.getElementById("nav-help-badge");
  if (c && b) { c.hidden = b.hidden; c.textContent = b.textContent || ""; }
  place();
  if (focusFirst) (menuItems().find((x) => x.getAttribute("aria-checked") === "true") || menuItems()[0])?.focus();
}
onAction("toggle-profile", (_el, ev) => {
  const m = menu();
  if (!m) return;
  if (m.hidden) openProfile((ev as MouseEvent).detail === 0);   // opened from the keyboard: focus goes into the menu
  else closeProfile();
});
onAction("profile-switch", async (el) => {
  const orgId = el.dataset.org || "";
  if (!orgId || orgId === state.session.organisation?.id) { closeProfile(true); return; }
  try { await switchOrganisation(orgId); } catch (err) { toast((err as Error).message, "error"); }
});
// A click anywhere else, or choosing an item, closes it, straight away (before the item's page opens, so a page kept
// for going back, such as the one Help returns to, never keeps the menu open).
document.addEventListener("click", (e) => {
  const t = e.target as HTMLElement;
  if (t.closest?.("#profile-strip")) return;
  if (t.closest?.("#profile-menu .falcon-profile-item") || !t.closest?.("#profile-menu")) closeProfile();
}, true);
document.addEventListener("keydown", (e) => {
  const m = menu();
  if (!m || m.hidden) {
    if ((e.key === "ArrowUp" || e.key === "ArrowDown") && document.activeElement === strip()) { e.preventDefault(); openProfile(true); }
    return;
  }
  if (e.key === "Escape") { e.preventDefault(); closeProfile(true); return; }
  const list = menuItems();
  const i = list.indexOf(document.activeElement as HTMLElement);
  const go = (n: number) => { e.preventDefault(); list[(n + list.length) % list.length]?.focus(); };
  if (e.key === "ArrowDown") go(i + 1);
  else if (e.key === "ArrowUp") go(i < 0 ? list.length - 1 : i - 1);
  else if (e.key === "Home") go(0);
  else if (e.key === "End") go(list.length - 1);
  else if (e.key === "Tab" && i >= 0 && ((e.shiftKey && i === 0) || (!e.shiftKey && i === list.length - 1))) closeProfile();
});
window.addEventListener("resize", () => { const m = menu(); if (m && !m.hidden) place(); });
